import React, { useContext } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import cx from 'classnames'

import { Testable } from '@cred/react-testable'
import { component as spContext, product as spProduct } from '@cred/react-analytics/contexts'
import { currency, number } from '@cred/shared/formatters'
import { withStyles } from '@cred/react-theme'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'

import { containsHostname } from 'utils/urls'
import useBreakpointWidth from 'common/hooks/useBreakpointWidth'
import CtaButton from 'components/cta-button'
import HelpIconWithModal from 'components/help-icon-with-modal'
import WidgetContext from 'components/widget-context'

import { termsHeaderLabelShortVersion } from '../../common/options'
import { loanTypeLabel } from './MortgageCombinedTile.utils'
import CredibleRating from '../../credible-rating/CredibleRating'
import RatesTableTooltip from '../../rates-table-tooltip/RatesTableTooltip'
import lenderDashboardImages from '../lender-logos'
import ClosingCostsModal from '../closing-costs-modal/ClosingCostModal'
import PaymentsModal from '../payments-modal'
import defaultStyles from './styles.module.scss'

const APT_TOOLTIP_TEXT = 'Annual Percentage Rate'

const mapStateToProps = ({ submission }) => ({ submission })

const MortgageCombinedTile = compose(
  withSnowplowContexts,
  connect(mapStateToProps),
  withStyles(defaultStyles),
)((props) => {
  const {
    product: {
      uuid,
      externalProductId,
      lender: { name, slug, nmls, purchaseRating },
      monthlyPayment,
      apr,
      rate,
      productType,
      loanType,
      closingCost,
    },
    onGetStarted,
    dashboardContexts,
    styles,
    loading,
    index,
  } = props

  const isNotMobile = !useBreakpointWidth('mobile')
  const infoFontType = isNotMobile ? 'medium' : 'bodyLarge'

  const { parentPage } = useContext(WidgetContext)
  const hideCredibleDisclaimer = containsHostname('credible.com', parentPage?.partnerURL)

  return (
    <Testable
      className={cx(styles.container, loading && styles.loadingContainer)}
      key={uuid}
      testId="product"
      id={externalProductId}
      data-test-id={`rateTableTile_${slug}`}
    >
      <div className={styles.firstRow}>
        <div className={styles.lenderSectionLeft}>
          <div className={styles.lenderLogoWrapper}>
            <div
              className={cx('flex', loading && styles.loadingBlock, {
                [styles.rocketMortgage]: slug === 'rocket_mortgage',
              })}
            >
              <img
                className={cx(styles.itemLenderLogo, {
                  [styles.rocketMortgage]: slug === 'rocket_mortgage',
                })}
                src={lenderDashboardImages(slug, !isNotMobile)}
                alt={name}
                height={16}
              />
            </div>
            <p
              className={cx(
                'disclaimer neutral500',
                styles.nmlsText,
                loading && styles.loadingBlock,
              )}
            >
              NMLS #{nmls}
            </p>
          </div>
          <CredibleRating
            rating={purchaseRating}
            loadingClassName={loading ? styles.loadingBlock : ''}
            interactive
            id={uuid}
          />
        </div>
        <div className={cx(styles.lenderSectionRight, loading && styles.loadingBlock)}>
          <p className="disclaimer neutral500">{termsHeaderLabelShortVersion[productType]}</p>
          <p className="disclaimer neutral500">{loanTypeLabel[loanType]}</p>
        </div>
      </div>

      <div className={styles.productDataAndButton}>
        <div className={styles.productData}>
          <Testable className={styles.rateAndApr} testId="product-details">
            <Testable testId="rate-details">
              <Testable testId="rate">
                <p
                  className={cx(
                    'flex bold',
                    infoFontType,
                    styles.arapeyFontFamily,
                    loading && styles.loadingBlock,
                  )}
                >
                  {number(rate || 0, 3)}
                  <span className={cx(styles.units, 'disclaimer bold')}>%</span>
                </p>
                <p className={cx('disclaimer neutral500', loading && styles.loadingBlock)}>Rate</p>
              </Testable>
            </Testable>
            <Testable testId="apr-details">
              <Testable testId="amount">
                <p
                  className={cx(
                    'flex bold',
                    infoFontType,
                    styles.arapeyFontFamily,
                    loading && styles.loadingBlock,
                  )}
                >
                  {number(apr || 0, 3)}
                  <span className={cx('disclaimer bold', styles.units)}>%</span>
                </p>
                <div className="flex">
                  <p className={cx('disclaimer neutral500', loading && styles.loadingBlock)}>APR</p>
                  <RatesTableTooltip
                    loadingClassName={loading ? styles.loadingBlock : ''}
                    analyticsOptions={{ contentSubject: 'Annual Percentage Rate Tooltip' }}
                    triggerClassName={styles.tooltipTrigger}
                  >
                    <p className="neutral500">{APT_TOOLTIP_TEXT}</p>
                  </RatesTableTooltip>
                </div>
              </Testable>
            </Testable>
          </Testable>

          <div className={styles.divider} />

          <Testable
            testId="mortgage-combined-tile-details"
            className={styles.moPaymentAndClosingCosts}
          >
            <Testable testId="monthly-payment-details">
              <p
                className={cx(
                  'bold',
                  infoFontType,
                  styles.moPayment,
                  styles.arapeyFontFamily,
                  loading && styles.loadingBlock,
                )}
              >
                {currency(monthlyPayment)}
                <span className={cx(styles.units, 'disclaimer semiBold neutral500')}>/mo</span>
              </p>
              <div className={styles.rowWithHelpIcon}>
                <p className={cx('disclaimer neutral500', loading && styles.loadingBlock)}>
                  Payment
                </p>
                <HelpIconWithModal
                  loadingClassName={loading ? styles.loadingBlock : ''}
                  modalName={`payments-modal-${uuid}-${index}`}
                  color="neutral700"
                  sdkScroll
                  modalComponent={PaymentsModal}
                />
              </div>
            </Testable>

            <Testable testId="fees-details">
              <p
                className={cx(
                  'bold',
                  infoFontType,
                  styles.moPayment,
                  styles.arapeyFontFamily,
                  loading && styles.loadingBlock,
                )}
              >
                {currency((closingCost < 0 ? 0 : closingCost) || 0)}{' '}
              </p>
              <div className={styles.rowWithHelpIcon}>
                <p className={cx('disclaimer neutral500', loading && styles.loadingBlock)}>
                  Closing costs
                </p>
                <HelpIconWithModal
                  loadingClassName={loading ? styles.loadingBlock : ''}
                  modalName={`closing-cost-modal-${uuid}-${index}`}
                  color="neutral700"
                  sdkScroll
                  modalComponent={ClosingCostsModal}
                />
              </div>
            </Testable>
          </Testable>
        </div>

        <Testable testId="mortgage-combined-tile-cta">
          <CtaButton
            className={cx(
              styles.ctaMobile,
              styles.submitButtonLoadingBlock,
              loading && styles.loadingBlock,
            )}
            onClick={() => onGetStarted(slug)}
            analyticsOptions={{ text: 'Continue' }}
            analyticsContexts={[
              ...dashboardContexts,
              spProduct.analytics({
                loanProductId: `${externalProductId}`,
                eligibilityProductId: '0',
              }),
              spContext.analytics({ name: 'Product tile', description: `${name}, ${rate}` }),
            ]}
          >
            Continue
            <i className="icon icon-chevron-right" />
          </CtaButton>
          {!hideCredibleDisclaimer && (
            <p
              className={cx(
                `mt-8 disclaimer neutral500`,
                styles.ctaModifierMobile,
                loading && styles.loadingBlock,
              )}
            >
              On Credible’s Website
            </p>
          )}
        </Testable>
      </div>
    </Testable>
  )
})

MortgageCombinedTile.propTypes = {
  formData: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired,
  onGetStarted: PropTypes.func.isRequired,
  dashboardContexts: PropTypes.array.isRequired,
  styles: PropTypes.object,
  submission: PropTypes.shape({
    downPayment: PropTypes.number,
    estimatedValue: PropTypes.number,
    loanAmount: PropTypes.number,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
}

export default MortgageCombinedTile
